import { useState } from 'react';
import { APP_REVISION } from '../../lib/appRevision';
import UserGuideQRCard from '../../components/UserGuideQRCard';
// Alias — same card, different placement (renamed for clarity on this page)
const HelpQRCard = UserGuideQRCard;
import { 
  Question, Book, MagnifyingGlass, CaretDown, CaretUp, Lightbulb,
  Gear, Users, Money, ShoppingCart, ChartLineUp, Warehouse, Calendar,
  Shield, ChatCircle, Kanban, FileText, CloudArrowUp, Receipt,
  Sparkle, NotePencil, Calculator, ClipboardText, Handshake
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";

const MODULES = [
  {
    id: 'agreements',
    name: 'Agreements (AMC / Service Contracts / SLA) — NEW',
    icon: Handshake,
    description: 'AMC, service agreements, one-time contracts, project contracts, SLAs — AI-drafted with branded PDF',
    features: [
      '7 agreement types: Comprehensive AMC, Non-Comprehensive AMC, Quarterly/Half-Yearly Maintenance, One-time Service Contract, Project Contract, SLA',
      '7 trades multi-select: HVAC / Electrical / Plumbing / Fire-fighting / BMS / Generator / Solar',
      'AI-drafted Preamble, Scope of Work, Major + Minor Checklists, T&C, Termination Clause (Gemini)',
      'Pre-seeded checklist templates per trade (start from template OR start blank)',
      'Equipment multi-select (AHU, FAHU, FCU, Pumps, Chiller, Generator, VRV/VRF, Solar, BMS, Fire Pump, etc.)',
      'Bill of Quantities (BOQ) table with auto-calculated amounts',
      'Payment schedules: Annual advance / Half-yearly / Quarterly / Monthly / Milestone / Custom',
      'Auto-generated payment-term instalments on save (date-shifted across the period)',
      'One-click "Generate Invoice" from any payment term — creates a linked invoice in INV-####',
      'Auto-renew toggle + Escalation % + Renewal notice days',
      'Renew button (carries scope/T&C/sign-off, shifts dates, applies escalation, marks source as renewed)',
      'Duplicate button (creates a fresh draft copy)',
      'Status flow: Draft → Pending → Active → Renewed / Expired / Terminated',
      'Termination clause + Termination reason capture',
      'Two signature blocks: Contractor + Client, each with Name + Position + Company + Date',
      'Branded multi-layout PDF: Classic / Modern Band / Minimalist (company logo + letterhead)',
      'Search by AGR#, client, site, title + filters by status, type, trade, expiring-within-60-days',
      'Spare parts INCLUDED / EXCLUDED lists + custom Exclusions + Client scope bullet lists'
    ]
  },
  {
    id: 'service-reports',
    name: 'Service Reports (Site Work Handover) — NEW',
    icon: ClipboardText,
    description: 'Site team work-completion form with tickable checklist, materials used, signatures and branded PDF',
    features: [
      'Created from any saved Job Order (4th tab → "New Service Report")',
      'Pre-fills checklist from the parent Job Order — site team ticks off as completed',
      'Work-done narrative (multi-line)',
      'Materials Used table: Name + Qty + Unit (Nos / Mtr / Kg / Litre) + Notes',
      'Work Attended By table: Name + Role (Lead Technician / Helper / Supervisor)',
      'Before / After / general photo uploads (embedded in PDF as full-width photos)',
      'Comments / observations free-text',
      'Next Visit toggle with Date + Reason (highlighted amber block on PDF)',
      'Customer Representative sign-off — name + touch-canvas signature (mouse/finger drawing)',
      'Status: Draft → Submitted (for review) → Approved (closed)',
      'Branded multi-layout PDF with company logo and letterhead — downloadable for on-site handover'
    ]
  },
  {
    id: 'job-orders',
    name: 'Job Orders (Field Service) — NEW',
    icon: Gear,
    description: 'Field/site work orders & PPM with AI checklists, MoS & Risk Assessment',
    features: [
      'Create one-time service or PPM (Planned Preventive Maintenance) orders',
      'Multi-select equipment (AHU, FAHU, FCU, Pumps, Chillers, Generators, VRV/VRF, Cooling Towers, etc. + custom)',
      'AI-generated equipment checklist (Gemini) — Safety & PPE / Visual / Mechanical / Electrical / Performance / Cleaning sections',
      'AI-drafted Method of Statement and Risk Assessment with Likelihood × Severity scoring',
      'Before / After picture uploads + arbitrary attachments (PDF, Word, Excel)',
      '5-stage workflow: Draft → Assigned → In Progress → Completed by Site → Approved',
      'Link to Customer LPO, Invoice, Project Reference',
      'Branded multi-layout PDF report (Classic / Modern Band / Minimalist / Compact)',
      'Signature blocks for Site Engineer and Client handover'
    ]
  },
  {
    id: 'dashboard',
    name: 'Dashboard',
    icon: ChartLineUp,
    description: 'Overview of your business metrics and KPIs',
    features: [
      'View revenue, expenses, and profit at a glance',
      'Track accounts receivable and payable',
      'Monitor sales trends and forecasts',
      'Access quick actions for common tasks'
    ]
  },
  {
    id: 'accounts',
    name: 'Accounts & Ledger',
    icon: Money,
    description: 'Double-entry accounting and financial management',
    features: [
      'Chart of Accounts with asset, liability, equity, income, expense accounts',
      'Journal entries with automatic balancing',
      'Account drill-down to see all transactions',
      'Bank reconciliation for cash accounts',
      'Trial balance and account summaries'
    ]
  },
  {
    id: 'sales',
    name: 'Sales & Invoicing',
    icon: Receipt,
    description: 'Manage customers, quotations, and invoices',
    features: [
      'Create and manage customer contacts (with WhatsApp, alt phone/email, bank details)',
      'Generate professional quotations with PDF export, page numbers, and clean pagination',
      'AI BOQ Upload — extract line items from Excel/CSV/Word/PDF/image with one click',
      'Per-line Specification (brand/model/specs) and Unit dropdown (Nos/Mtr/Litre/Lot/Kg + custom)',
      'Project Reference and Project Location on every doc, surfaced on lists, search and PDFs',
      'Convert quotations to invoices with an approval guard for non-approved quotes',
      'Custom payment terms (free-form) plus all standard presets',
      'Track payment status and overdue invoices',
      'Apply discount at the quotation total only — no more per-line discount confusion'
    ]
  },
  {
    id: 'purchases',
    name: 'Purchases & GRN',
    icon: ShoppingCart,
    description: 'Supplier management and procurement',
    features: [
      'Manage supplier contacts and information',
      'Create Request for Quotations (RFQ)',
      'Generate Purchase Orders from RFQs',
      'Record Goods Receipt Notes (GRN)',
      'PDF generation for all purchase documents'
    ]
  },
  {
    id: 'hr',
    name: 'HR & Employees',
    icon: Users,
    description: 'Human resources and employee management',
    features: [
      'Maintain employee records with personal and bank details',
      'Track visa, passport, and document expiry dates',
      'Manage departments and designations',
      'Configure salary structure with allowances',
      'Compliance alerts for expiring documents'
    ]
  },
  {
    id: 'payroll',
    name: 'Payroll',
    icon: Money,
    description: 'Salary processing with overtime and deductions',
    features: [
      'Monthly payroll runs for all employees',
      'Overtime calculation (Regular, Weekend, Holiday rates)',
      'Deductions: Loans, Absences, Custom deductions',
      'Leave salary and encashment',
      'End of Service (EOS) benefits calculator',
      'PDF payslip generation',
      'Approval workflow for payroll processing'
    ]
  },
  {
    id: 'inventory',
    name: 'Inventory v2 (Multi-warehouse)',
    icon: Warehouse,
    description: 'Stock management with multi-warehouse, transfers and bin cards',
    features: [
      'Track stock levels per warehouse (Main / Branch / Site / Van)',
      'Default "Main Store" auto-created; add unlimited additional warehouses',
      'Stock Transfers between warehouses with Draft → In Transit → Completed workflow',
      'Per-item Bin Card showing every stock-in / stock-out movement (Purchase, Sales, Transfer, Adjustment) with running balance',
      'Stock valuation (FIFO, Average Cost)',
      'Low stock alerts (per warehouse)',
      'Universal report toolbar — PDF / Excel exports for Stock Summary, Bin Card, Transfers',
      'Document Summary Bar with Stock Value / Low Stock / Out of Stock KPIs'
    ]
  },
  {
    id: 'tasks',
    name: 'Tasks & Kanban',
    icon: Kanban,
    description: 'Task management with drag-and-drop boards',
    features: [
      'Create task boards for different projects',
      'Kanban view with To Do, In Progress, Review, Done columns',
      'Drag and drop tasks between columns',
      'Assign tasks to team members',
      'Set priorities and due dates',
      'Add tags for categorization'
    ]
  },
  {
    id: 'chat',
    name: 'Internal Chat',
    icon: ChatCircle,
    description: 'Team communication and messaging',
    features: [
      'Direct messages with team members',
      'Create group chats for departments/projects',
      'Real-time message updates',
      'Unread message indicators'
    ]
  },
  {
    id: 'storage',
    name: 'Document Storage',
    icon: FileText,
    description: 'Centralized document management',
    features: [
      'Upload and organize documents in folders',
      'Categorize by type (contracts, invoices, etc.)',
      'Preview and download documents',
      'Link documents to employees or records'
    ]
  },
  {
    id: 'calendar',
    name: 'Calendar & Reminders',
    icon: Calendar,
    description: 'Schedule events and track deadlines',
    features: [
      'Calendar view of all events',
      'System-generated reminders for expiring documents',
      'PDC (Post-Dated Cheque) tracking',
      'Create custom reminders',
      'Filter by event type'
    ]
  },
  {
    id: 'compliance',
    name: 'Compliance',
    icon: Shield,
    description: 'Track regulatory compliance and documents',
    features: [
      'Monitor license and permit expiry',
      'Upload compliance documents',
      'Automated alerts for renewals',
      'Compliance status dashboard'
    ]
  },
  {
    id: 'audit',
    name: 'Audit & Backup',
    icon: CloudArrowUp,
    description: 'System audit logs and data backup',
    features: [
      'View all admin actions and changes',
      'Create manual backups',
      'Automated daily backups',
      '30-day backup retention',
      'Cloud storage support (AWS S3, Google Cloud)',
      'Restore from backup'
    ]
  }
];

const FAQS = [
  {
    category: `Part Invoicing (NEW ${APP_REVISION.version})`,
    questions: [
      {
        q: 'What is Part Invoicing and when should I use it?',
        a: 'Part Invoicing lets you bill a job in slices instead of one big invoice. Each part carries an Approved % (or Approved Qty) per line. All parts share the same base invoice number with a numeric suffix — INV-0025-1, INV-0025-2, INV-0025-3 … Cumulative-% is capped at 100 per line, so the chain automatically closes when every line reaches 100 %. Use it for progress billing on long projects, retention releases, or any staged delivery.'
      },
      {
        q: 'How do I create the FIRST part invoice from a quotation?',
        a: 'Sales → Quotations tab → row menu (⋯) on an approved / sent quotation → "Create Part Invoice…". A dialog opens with each quotation line pre-loaded, showing "Already 0.0 % / 100 % left". Enter the Approved % for this round (0-100) and pick an Apply mode: Amount (scales the line total by %) or Qty (scales the quantity by %). Click Create — you get INV-0025-1 with the correct tax computed AFTER the % is applied.'
      },
      {
        q: 'How do I raise a part invoice WITHOUT a quotation (project-driven)?',
        a: 'Invoices tab → click "New Part Invoice (Project)" (top-right of the filter bar). Step 1: pick the customer and enter a Project Reference — this pair identifies the chain, so use the same reference on every round. Step 2: Round 1 shows an empty table where you manually enter Original Qty, Original Rate and Approved Qty per line. Round 2+ auto-loads the anchor scope from round 1 and only lets you bill up to each line\'s Balance qty.'
      },
      {
        q: 'How is the invoice amount calculated with Approved %?',
        a: 'Approved Qty × Original Rate = the line amount for this round. Tax is computed AFTER that scaling, so VAT reflects only the part-billed amount. Example: original scope 10 units × 500 AED = 5000. Round 1 with 40 % approved (amount mode) → 2000 base + 100 VAT (@5%) = 2100. Round 2 with the remaining 60 % → 3000 base + 150 VAT = 3150. Total across both parts = 5250, exactly what one full 5000+5% invoice would have been.'
      },
      {
        q: 'How does the system show what\'s already invoiced when I make a second part?',
        a: 'Open the same quotation or project chain and click "Create Part Invoice" again. The preview loads each line with three columns: Already (%/qty already invoiced across all prior parts), Balance (100 % / balance qty remaining), and Approved (this round, capped at Balance). You cannot exceed the balance — the backend rejects any request that would push the cumulative past 100 %.'
      },
      {
        q: 'Can I edit the Original Qty or Rate on a later round?',
        a: 'Yes, in the project-driven dialog (Path B). Round 2+ lets you type over the anchor Original Qty/Rate. A "base was: X" note appears inline. The deviation is captured on the invoice\'s audit trail (`part_deviation_notes`). Important: the balance ceiling stays anchored to Round 1\'s numbers, so the scope contract stays stable. Editing is for correcting a genuine mistake, not for expanding scope.'
      },
      {
        q: 'What about the Approved Qty and Approved % — do I enter both?',
        a: 'Edit either one — the other auto-fills. Approved % = Approved Qty / Original Qty × 100. Handy when the client approves the round as a percentage (e.g. "50 % of Phase 2") — you type 50 in the % column and the qty updates. Or when the client says "20 units" — type it in the qty column and the % updates.'
      },
      {
        q: 'How is the invoice numbering assigned?',
        a: 'The FIRST part invoice mints a fresh base number (e.g. INV-0025) and appends the round: INV-0025-1. Every subsequent part reuses the base with the next sequence: INV-0025-2, INV-0025-3, and so on. A regular non-part invoice raised in parallel gets its own fresh base number — no collision with the part chain.'
      },
      {
        q: 'What happens to Advance payments?',
        a: 'Path A (Quotation-driven): all receipt vouchers already booked against the quotation appear in an "Advance Pool" card. Each round shows the pool total, how much has been applied to earlier parts, and how much is available for this round. You type how much of the available pool to apply. Only vouchers not yet attributed to another invoice get re-linked, so Statement-of-Account attribution on earlier parts stays put. Path B (Project-driven): enter a manual advance amount for this invoice.'
      },
      {
        q: 'Does the accounting work correctly for part invoices?',
        a: 'Yes — part invoices flow through the SAME accounting path as any regular invoice. Approve one and it: (a) appears on GET /invoices with is_part_invoice + part_sequence + base_invoice_number metadata for badges, (b) creates a balanced Journal Entry (DR Accounts Receivable · CR Sales Revenue · CR Tax Payable), (c) shows on the customer Statement of Account with correct debit and running balance, (d) counts toward Dashboard KPIs, Trial Balance, P&L exactly like any other invoice.'
      },
      {
        q: 'What if the browser closes while I\'m filling a part invoice?',
        a: 'The Project Part Invoice dialog autosaves every 800 ms while you type. Reopen the Invoices tab → click "New Part Invoice (Project)" — you\'ll see a green "Restore draft" banner with the saved timestamp. Click Restore to continue where you left off; Discard to start fresh. A native browser prompt also catches accidental tab-close / navigation while the form is dirty.'
      },
      {
        q: 'How do I know when a chain is fully invoiced?',
        a: 'When every line reaches 100 % cumulative, the last part invoice returns is_fully_invoiced=true and the parent quotation flips to status = "invoiced" (Path A) or the project chain is marked fully invoiced in the chains listing (Path B). Intermediate rounds mark the quotation as "part_invoiced".'
      },
      {
        q: 'Can I still add ad-hoc lines that weren\'t on the original quotation?',
        a: 'Yes — every part-invoice dialog has a "+ Add Extra Line" button. Extra lines are billable in that round and become new anchors for subsequent rounds (project flow). They\'re excluded from the cumulative-% tracking of the original quotation lines, so adding an extra doesn\'t affect the balance % on other lines.'
      },
    ]
  },
  {
    category: 'End-to-End Workflow (Sales → Cash)',
    questions: [
      {
        q: 'What is the standard BizCore workflow from enquiry to final payment?',
        a: '13 stages, all linked: ① Quotation & Costing (Sales → Quotations + Costing Sheet for supplier RFQ costs) → ② Internal Approval (Approvals → Pending) → ③ Send to Client (PDF / WhatsApp / Email) → ④ Capture Client Acceptance (LPO #, agreement ref, even verbal — store in the quotation\'s Client Acceptance block) → ⑤ Issue LPO/PO to Supplier or Sub-contractor (Purchases → New PO) → ⑥ Proforma or Tax Invoice for advance (Quotation kebab → Convert to Invoice → pick Invoice Type) → ⑦ Job Order with scope, checklist, MoS, T&Cs (Operations → Job Orders) → ⑧ Execute on site (status: Draft → Assigned → In Progress → Completed) → ⑨ Job Completion / Delivery Report with photos + signature → ⑩ Progress / Final Invoice (Billing Type = Progress / Part-billing with retention + advance deductions) → ⑪ Mark Paid into the correct Bank account (Mark-as-Paid dialog asks for Payment Mode + Bank) → ⑫ Pay supplier / sub-contractor (Payment Voucher → Manager approves → JE posts) → ⑬ Reconcile (Dashboard banner → "Reconcile Now" weekly to catch any drift).'
      },
      {
        q: 'Where do I record the supplier / sub-contractor costs I receive in RFQ replies?',
        a: 'Open the Quotation → Costing Sheet tab. Add a line per supplier quote (Item, Supplier, Cost, Markup %). The Sales Price and Gross Margin update in real time. This is your internal cost sheet — it never appears on the customer-facing PDF.'
      },
      {
        q: 'My client gave verbal approval — how do I capture that without an LPO yet?',
        a: 'On the Quotation, fill the "Client Acceptance" block with: Acceptance Date, Acceptance Type (LPO / Email / Verbal / Agreement), Reference / Notes ("Verbal approval from Mr. X on call dated …"). Save. This satisfies your audit trail even without a paper LPO.'
      },
      {
        q: 'When should I use Proforma Invoice vs Tax Invoice?',
        a: 'Proforma Invoice — pre-agreement / pre-contract advance request. Amber "Not a Tax Invoice" badge, no QR code, no VAT booking. Use this when the client has accepted verbally but the LPO/agreement is not yet signed. Tax Invoice — once the contract is firm. Carries the 5% VAT, QR code for verification, full audit posting (DR AR / CR Revenue / CR Tax). Both modes are picked from the Invoice Type dropdown on the Create Invoice form.'
      },
      {
        q: 'How do I bill in stages (progress billing) and apply retention?',
        a: 'Set Billing Type = "Progress / Part-billing Invoice" on the invoice form. Pick Overall % (e.g. 30% milestone) OR Per-line %. The Deductions block (under Totals) lets you enter Retention %, Advance Received, Previously Paid. Order: Subtotal → less Retention → Net Taxable → +VAT → Gross → less Advance → less Previously Paid → Current Payable. The PDF prints each step in mono font so the math is auditable. For the final retention release, raise a new invoice with the retention-release amount only.'
      },
      {
        q: 'Why does the Mark-as-Paid dialog now ask for a Bank Account?',
        a: 'Because the deposit needs to post to the correct ledger account so your bank statement, Trial Balance and Dashboard reflect reality. Pick the Bank Account that actually received the money (1010 Bank Account, or any other you have set up). The auto-created Receipt Voucher posts DR Bank / CR Accounts Receivable. If you skip this step the payment defaults to Cash (1000) and you will have to fix it later via "Reconcile Now".'
      },
      {
        q: 'I deleted a duplicate Receipt Voucher and now my invoice still shows Paid but the Bank balance is wrong. What do I do?',
        a: 'Open the Dashboard. A RED "Finance Reconciliation" banner will list the drift. Click "Reconcile Now" — it (a) recomputes amount_paid from the active receipt vouchers, (b) reverts the invoice back to "Approved" status so you can re-record the payment to the correct bank, (c) voids the JE left orphaned by the deleted voucher. After it finishes, re-run "Mark as Paid" on the invoice and pick the right Bank Account.'
      },
      {
        q: 'How do I issue payment to my supplier / sub-contractor?',
        a: 'Two paths: (1) From a Bill — Purchases → open the supplier Bill → "Pay Bill" — pre-fills the payment voucher with the bill amount. (2) From scratch — Vouchers → New Payment Voucher — pick the supplier, fill amount + narration, pick the Bank/Cash account from which money goes out. Submit for Approval; once the Manager approves, the JE posts (DR Accounts Payable / CR Bank) and the bank balance updates.'
      },
      {
        q: 'Where is the Job Completion Report / Delivery Report?',
        a: 'Two ways to reach it: (1) Inside any Job Order → 4th tab "Service Reports" — fills the work-completion document linked to the JO, with the JO\'s checklist pre-loaded. (2) Reports → New Report → pick Report Type = "Job Completion Report" (or "Delivery Report" for material delivery) — standalone with Client + LPO + Signature canvas. Both produce a branded PDF with your logo, before/after photos, materials list, attended personnel and the customer-rep signature.'
      },
      {
        q: 'Can I open this workflow guide from any screen?',
        a: 'Yes — click the ? (Help) icon in the topbar, available on every page. It opens the User Guide directly on this workflow section. You can also open it from the sidebar → Help & FAQ.'
      }
    ]
  },

  {
    category: 'Agreements (AMC / Service Contracts / SLA)',
    questions: [
      {
        q: 'What is the Agreements module for?',
        a: 'A complete contract-management tool: draft, sign, manage and renew Annual Maintenance Agreements (AMCs), Service Agreements, One-time Service Contracts, Project Contracts and SLAs. Each agreement carries its own Scope of Work, Equipment list, Major/Minor checklists, BOQ, Payment schedule, Terms & Conditions, Termination clause and signatures.'
      },
      {
        q: 'How do I create a new AMC?',
        a: 'Operations → Agreements → "New Agreement". Tab 1 (Details): pick a Type (e.g. AMC — Comprehensive), select trades (HVAC, Electrical…), pick a client from the searchable dropdown, set Start/End dates, Auto-renew toggle + Escalation %. Tick "Start with template checklists" to pre-seed Major + Minor checklists per trade — or untick to start blank and let AI generate. Click Save → AGR-#### is assigned, status = Draft.'
      },
      {
        q: 'What does "Start with template checklists" do?',
        a: 'When ticked, the system pre-seeds your selected trades\' default Major + Minor service checklists out-of-the-box. For example, picking HVAC seeds annual coil chemical wash, compressor vibration analysis, refrigerant top-up (Major) + monthly filter clean, drain flush, belt tension (Minor). You can then edit, add, or remove items. Untick to start blank.'
      },
      {
        q: 'How does AI drafting work for agreements?',
        a: 'Four AI buttons — each uses Gemini (Emergent LLM Key, no API key needed): (1) "AI draft" on Preamble → 2-3 paragraph formal opening. (2) "AI draft" on Scope of Work → detailed scope with PPM activities, breakdowns, deliverables, safety. (3) "AI generate" on Checklists → 10+ items per trade split Major/Minor. (4) "AI draft" on T&C and Termination Clause → UAE-style numbered clauses covering payment, spares, force majeure, indemnity, dispute resolution, termination for convenience/breach/insolvency.'
      },
      {
        q: 'How is the Payment Schedule auto-generated?',
        a: 'Pick a schedule on Tab 5 (Annual advance / Half-yearly / Quarterly / Monthly / Milestone / Custom). On first save, the system auto-builds the instalment rows based on Start date, End date and Total value. Each row has Label / Due date / % / Amount and is freely editable. For example, "Quarterly" splits the total into 4 equal parts dated at 0%, 25%, 50%, 75% of the agreement period.'
      },
      {
        q: 'How do I generate the next invoice from an agreement?',
        a: 'After the agreement is Active, open it → Tab 5 (Commercials & Payment) → click "Generate invoice" on any payment-term row. A linked invoice (INV-####) is created in your Invoices module with the agreement number in the notes + linked_agreement_id. The payment-term row then shows the INV-#### badge. Generating twice on the same row is blocked.'
      },
      {
        q: 'How does Renewal work?',
        a: 'For Active or Expired agreements, click the Renew button (or the kebab Renew action) → a new Draft agreement is created with: (1) is_renewal_of pointing to the old one, (2) dates shifted by the original term length, (3) Annual + Total value adjusted by the Escalation %, (4) Fresh payment terms built for the new period. The OLD agreement is then marked Status = Renewed.'
      },
      {
        q: 'How do I terminate an agreement early?',
        a: 'On the Agreements list → click the X icon (red) → enter a termination reason. The agreement moves to Status = Terminated with timestamp and reason captured. You can still download the PDF (showing the termination clause that was agreed). Terminated agreements cannot be edited — Duplicate to start fresh.'
      },
      {
        q: 'Can I have multi-site / multi-property agreements?',
        a: 'Yes — on Tab 1, the "Primary site address" is the main contractual address, and "Additional sites" lets you add multiple site locations covered by the same agreement (e.g. one agreement covers Tower A + Tower B + Villa Compound). All sites appear on the PDF.'
      },
      {
        q: 'How do I capture signatures?',
        a: 'Tab 7 (Sign-off). Two signatory blocks: Contractor (your team — e.g. General Manager) and Client (e.g. Facilities Manager). Each captures Name + Position/Designation + For & on behalf of (Company) + Date. Optionally, you can also paste a signature image data-URL (canvas signature support coming in next iteration). The PDF prints both blocks with signature placeholders.'
      },
      {
        q: 'How is "Spare parts INCLUDED vs EXCLUDED" different in the PDF?',
        a: 'Tab 2 has a "Spare parts INCLUDED in contract value" checkbox. When ticked = Comprehensive AMC (you supply all consumables + minor spares as part of the agreement value). When unticked = Non-Comprehensive (Client pays for spares separately). Use the Inclusion / Exclusion lists below to make this explicit on the PDF — e.g. "Filters, belts, fuses INCLUDED; Compressors, motors > 5kW EXCLUDED."'
      },
      {
        q: 'How do I duplicate an agreement?',
        a: 'List → Copy icon (or the form\'s Duplicate button). A new AGR-#### Draft is created with all your scope/T&C/equipment carried over but: invoice tracking reset, status reset to Draft, dates kept the same (edit before activation). Useful when onboarding multiple clients on the same boilerplate.'
      },
      {
        q: 'How do I find agreements expiring soon?',
        a: 'Top filter bar → tick "Expiring (60d)". The list narrows to active or renewed agreements with an end_date within the next 60 days — your renewal pipeline. (A dashboard widget summarising this count + a daily email digest are on the roadmap.)'
      },
      {
        q: 'What customer info is auto-populated?',
        a: 'When you pick a customer from the dropdown, the system auto-fills the customer address, TRN, contact phone and email from your Contacts master. Edit any of these inline for this specific agreement without changing the master record.'
      }
    ]
  },
  {
    category: 'Service Reports (Site team work-completion form) — NEW',
    questions: [
      {
        q: 'What is the Service Report module?',
        a: 'A Service Report is the document the SITE TEAM fills out after attending a Job Order. It carries the same checklist as the parent job (now tickable as items are completed), plus work-done narrative, materials used, people who attended, before/after photos, comments, optional next-visit date, and a touch-canvas signature from the customer representative. One Job Order can have many Service Reports (e.g. for recurring PPM visits).'
      },
      {
        q: 'How do I create a Service Report?',
        a: 'Open any saved Job Order → 4th tab "Service Reports" → click "New Service Report". The system pre-fills the checklist from the parent job and creates SR-#### in Draft status. You can then fill the 6 tabs: Checklist, Work Done, Materials, Attendants, Photos, Sign-off & Next Visit.'
      },
      {
        q: 'How does the customer sign on site?',
        a: 'Open the Service Report → Sign-off tab. The customer\'s representative types their Name and signs on the canvas with mouse or finger (touchscreen tablet/phone friendly). Click "Save" — the signature is stored as a PNG inside the report and embedded into the branded PDF. Click "Save & Download PDF" to email/WhatsApp the signed report to the office.'
      },
      {
        q: 'What goes in "Materials Used" vs "Attendants"?',
        a: 'Materials Used = consumables and spare parts used during the service (Filter, Gas, Lubricant, Bolts, etc.) with Qty and Unit (Nos / Mtr / Kg / Litre). Attendants = people who physically attended the job (Lead Technician, Helper, Supervisor) with Name + Role. Both appear as separate tables on the PDF.'
      },
      {
        q: 'Can I attach before/after photos to the Service Report (in addition to the Job Order)?',
        a: 'Yes — the Service Report has its OWN Photos tab with Before / After / Attachments uploads (separate from the parent Job Order\'s photos). Upload images and they\'re embedded in the SR PDF as full-width photos with captions. Attached PDFs/Word/Excel are listed in the appendix.'
      },
      {
        q: 'What\'s the difference between Job Order PDF and Service Report PDF?',
        a: 'Job Order PDF = the dispatch / work-order document sent to the technician BEFORE the work (scope, checklist to do, MoS, RA). Service Report PDF = the handover document AFTER the work (ticked checklist, work done, materials used, photos, customer signature). Both share your company branded letterhead with logo.'
      },
      {
        q: 'How does "Next Visit" work?',
        a: 'On the Sign-off tab, tick the "Next Visit" checkbox. Two extra fields appear: Date (when you should return) and Reason (e.g. "Next quarterly PPM" or "Replace contactor when part arrives"). The next-visit block appears as a highlighted amber box on the SR PDF so the customer signs off acknowledging the return visit.'
      }
    ]
  },
  {
    category: 'Customer Dropdown & Inline Add / Edit — NEW',
    questions: [
      {
        q: 'How do I pick a customer on a Job Order?',
        a: 'In the Job Order form (Details tab), the Customer field is a searchable dropdown — start typing a customer\'s name, company, phone or email and matching contacts appear. Click one to select. The customer\'s saved Address auto-populates the Site Location field (you can override).'
      },
      {
        q: 'How do I add a new customer without leaving the Job Order?',
        a: 'Click the + (UserPlus) icon next to the customer search box → an inline "Add New Customer" dialog opens. Fill Name (required), Company Name, TRN/Tax ID, Phone, Email, Address. Click "Add Customer" — the new contact is saved to your master Contacts list (visible in Clients / Customers tab too) and automatically selected on the current Job Order. No need to navigate away and come back.'
      },
      {
        q: 'Can I edit a customer\'s details from within a Job Order?',
        a: 'Yes — after selecting a customer, click the pencil icon next to the search box. The same dialog opens pre-filled with the customer\'s current data. Edit any field (e.g. updated phone, new TRN) and Save. Changes are pushed to the master Contacts list and reflected everywhere the customer is used (existing Quotations, Invoices, Job Orders).'
      }
    ]
  },
  {
    category: 'Job Orders — WhatsApp Share, PDF, Search',
    questions: [
      {
        q: 'Can I send a Job Order directly via WhatsApp?',
        a: 'Yes — each row on the Job Orders list has a WhatsApp icon (green). Click it → wa.me opens in a new tab with a pre-formatted message (JO#, Customer, Site, Scheduled date, Equipment, Status). If the selected customer has a saved phone number, the chat opens directly to that contact; otherwise it opens with the message text only so you can pick a contact. The PDF is not auto-attached — use "Download PDF" first then attach manually in WhatsApp (image/PDF).'
      },
      {
        q: 'How does the new Search bar work on the Job Orders list?',
        a: 'Top-left of the list — type any of: Job Number, Customer name, Site location, Assigned technician, or Equipment type. The list filters in real time. Combine with the Status dropdown (top-right) for narrower searches like "all approved jobs for Customer X".'
      },
      {
        q: 'Why does my Job Order PDF now show my company logo?',
        a: 'PDFs were updated to always render the standard company letterhead — logo + name + address + phone + email + TRN — at the top of every Job Order and Service Report, styled to match the layout you pick (Classic / Modern Band / Minimalist / Compact). To customise the logo, go to Settings → Company → upload Logo.'
      },
      {
        q: 'What does "Save & Download PDF" do (different from "Save")?',
        a: 'Save = saves the Job Order to the database and keeps the form open so you can continue editing or flip to the Service Reports tab. Save & Download PDF = saves AND immediately downloads the branded PDF in one click — perfect for dispatching a fresh job card to site without leaving the dialog.'
      }
    ]
  },
  {
    category: 'Job Orders (Field Service) — NEW',
    questions: [
      {
        q: 'What is the Job Orders module for?',
        a: 'It\'s BizCore\'s field service / maintenance work-order module — perfect for MEP, HVAC, facilities and contracting companies. Use it for one-time service calls AND for PPM (Planned Preventive Maintenance) schedules. Each job carries equipment-specific checklists, Method of Statement, Risk Assessment, before/after photos, signatures and a branded PDF report.'
      },
      {
        q: 'How do I create a new Job Order?',
        a: 'Operations → Job Orders → "New Job Order". Fill Customer, Site Location, Service Type (one-time / PPM), Scheduled Date and Priority. Pick the Equipment chips (AHU, FCU, Pumps, Chiller, Generator, etc.) — or add a custom equipment. Optionally link a Customer LPO# or existing Invoice. Write the Scope of Work and click Create. The system auto-assigns a JOB-#### number and the order is saved as Draft.'
      },
      {
        q: 'How does the AI Checklist work?',
        a: 'Inside the Job Order form, open the Checklist editor and pick an equipment from the "⚡ AI add for equipment…" dropdown. Gemini (via the Emergent LLM key) generates 5–10 actionable items per section: Safety & PPE, Visual Inspection, Mechanical, Electrical, Performance / Readings, and Cleaning. The depth adapts to whether you picked one-time or PPM. You can edit, add, or delete items afterwards.'
      },
      {
        q: 'How do I auto-generate the Method of Statement and Risk Assessment?',
        a: 'On the same Job Order form, click "✨ AI draft" on either the Method of Statement panel or the Risk Assessment panel. Gemini reads your Scope of Work + selected equipment + site location and drafts the full document (Purpose, Scope, Tools, PPE, Step-by-Step, Safety Controls, Emergency Procedures for MoS; or 5+ Hazards with Likelihood × Severity scoring and Control Measures for RA). Edit freely after generation.'
      },
      {
        q: 'How do I upload before/after pictures?',
        a: 'Save the Job Order first — the upload boxes only appear after creation. Re-open the order and you\'ll see three sections: Before Pictures, After Pictures, and Attachments. Click Upload and pick the file. Images embed inline as photos in the PDF; PDFs/Word/Excel attachments are listed in the appendix. Files are stored on disk AND replicated to MongoDB GridFS for production resilience.'
      },
      {
        q: 'What is the job status workflow?',
        a: 'Draft (creator edits) → Assigned (manager dispatches to site team) → In Progress (site team clicks Start on arrival) → Completed by Site (site team marks the work done) → Approved (manager/supervisor reviews and approves). At any of the first 3 steps the order can also be Cancelled. The status moves are recorded with names + timestamps and shown in the PDF\'s status timeline.'
      },
      {
        q: 'Where do I find the Job Order PDF?',
        a: 'On the Job Orders list, click the PDF icon on any row → the branded report downloads. Inside the Job Order dialog there\'s also a "Download PDF" button. The PDF uses the same multi-layout system as Invoices (Classic / Modern Band / Minimalist / Compact) — pick your preferred one on the "PDF layout" field.'
      },
      {
        q: 'Can I add new equipment types?',
        a: 'Yes — every company can extend the equipment list with custom types (e.g. "VFD Drive", "BMS Controller", "Solar Inverter"). They appear in the multi-select chips alongside the defaults. Add via the small + icon in the equipment selector (admins/managers only).'
      },
      {
        q: 'Does AI cost extra?',
        a: 'No. AI checklist / MoS / RA generation uses the Emergent LLM key shipped with your subscription — no separate API key is required, and there is no per-call fee on your side. Be aware of the platform key budget if you generate hundreds of jobs per day.'
      }
    ]
  },
  {
    category: 'Inventory v2 — Warehouses & Bin Cards',
    questions: [
      {
        q: 'How do I add a new warehouse / store / branch?',
        a: 'Inventory → Warehouses tab → "Add Warehouse". Pick a Type (Main / Branch / Site / Vehicle-Van), set Address, In-charge person, Contact phone, and optional GPS. A default "Main Store" was already auto-created for your company on first launch.'
      },
      {
        q: 'How do I transfer stock from Main Store to a Site warehouse?',
        a: 'Inventory → Transfers tab → "New Transfer". Pick From = Main Store, To = your site warehouse. Add items + quantities — the system blocks the transfer if From doesn\'t have enough on hand. Click Submit → status moves to In Transit. The receiver clicks "Receive" on arrival → status becomes Completed and stock moves automatically. Both warehouses\' Bin Cards reflect the movement.'
      },
      {
        q: 'What is a Bin Card?',
        a: 'Open any item card → Bin Card tab. It\'s the full stock-in / stock-out ledger for that item — across all warehouses — showing Opening Balance, every Purchase Receipt (GRN), Sales Issue (Invoice), Transfer In/Out, and Stock Adjustment, with running balance per warehouse. Filter by date range and warehouse. Export as PDF or Excel via the universal toolbar.'
      },
      {
        q: 'Can I delete a warehouse that has stock history?',
        a: 'You can soft-delete it (status changes to Inactive) but the history is preserved — Bin Cards remain auditable. To physically delete, you must first zero out the stock by transferring it elsewhere.'
      },
      {
        q: 'How is "default warehouse" used?',
        a: 'When you create a Purchase Order / GRN / Sales Invoice and don\'t explicitly pick a warehouse, the system uses the company\'s default warehouse. Set it on the Warehouses tab — only one can be default at a time.'
      }
    ]
  },
  {
    category: 'Quotation Revisions & Diff Viewer',
    questions: [
      {
        q: 'How do I save changes as a new revision instead of overwriting?',
        a: 'Sales → Quotations → kebab (⋮) → "Save as Revision". The original quotation\'s document number (e.g. QTN-0001) is kept, but a new revision tag (R1, R2, R3…) is added. The original revision is preserved unchanged — you can always go back and download R0\'s PDF.'
      },
      {
        q: 'Where do I see all revisions of a quotation?',
        a: 'On the Quotations list, toggle "Show Revisions" (top-right). The list expands to include every historical revision as its own row, displayed as QTN-0001 (R1), QTN-0001 (R2), etc. Click any row to open that exact version.'
      },
      {
        q: 'How do I compare two revisions?',
        a: 'In the Show Revisions list, tick the checkboxes on two revisions of the same quotation, then click "Compare". The Revision Diff Viewer opens — added line items show in green, removed in red, changed in amber. Header fields (customer, project, payment terms, validity) and totals are shown with before/after columns and the delta in AED.'
      },
      {
        q: 'Can I delete an old revision?',
        a: 'Soft-delete only (moves to Recycle Bin). You can\'t hard-delete from the main UI — keeping revisions is required for audit and compliance.'
      }
    ]
  },
  {
    category: 'Universal Report Toolbar (PDF / Excel)',
    questions: [
      {
        q: 'What is the toolbar at the top of every Reports / Inventory tab?',
        a: 'It\'s the Universal Report Toolbar. Every financial and inventory report (Trial Balance, P&L, Balance Sheet, Account Ledger, Stock Summary, Bin Cards, Transfers, Job Orders…) shares the same controls: Date Range picker, Search box, plus three buttons — View (preview), PDF (branded download), Excel (styled .xlsx).'
      },
      {
        q: 'Do the exports honour my current filter?',
        a: 'Yes. Type in the Search box or change the Date Range and the exports will only include matching rows. The PDF/Excel header always shows the active range and search query so you can audit the export later.'
      },
      {
        q: 'What does the Excel export look like?',
        a: 'A clean .xlsx file (openpyxl) with: a Summary sheet (totals, range, KPIs), styled coloured headers, frozen first row, auto-sized columns, currency formatting, and a Documents sheet listing every row in the filtered set.'
      },
      {
        q: 'What does the PDF export look like?',
        a: 'A branded PDF (WeasyPrint) with your company logo, TRN, address in the header. The data is rendered as a clean table, with subtotals and grand totals. Per-page footer shows company name, page X of Y, and the report name.'
      }
    ]
  },
  {
    category: 'Account Recovery & Admin Password Reset',
    questions: [
      {
        q: 'I forgot my password — how do I reset it without contacting admin?',
        a: 'On the login page, click "Forgot Password?". Enter your email, Date of Birth, and the answer to your Security Question. If all three match your profile, you\'ll be allowed to set a new password (link valid 15 min). Tip — set your DoB and Security Question now in Settings → Profile & Account Recovery so this works in an emergency.'
      },
      {
        q: 'My self-service recovery failed — what now?',
        a: 'Contact your Platform Admin (or info@sgkapps.com). They open Platform Admin → Users tab, click the 🔑 "Reset Password" icon on your row, and share the temporary password with you. The next time you log in you\'ll be forced to set a new permanent password.'
      },
      {
        q: 'My account got locked after wrong attempts and the temp password doesn\'t work.',
        a: 'The fixed Platform Admin Password Reset tool clears the lockout in the same action — but if you were sent a temp password BEFORE that fix shipped, ask the admin to reset it again. The new reset will also clear account_lockouts and login_attempts so you can sign in immediately.'
      },
      {
        q: 'I see "must change password" on next login — is that normal?',
        a: 'Yes — after any admin-issued reset, the system flags must_change_password = true on your account. After signing in with the temp password, you\'ll be prompted to set a new permanent password before you can use the app.'
      },
      {
        q: 'I changed my password but still can\'t see new features in production.',
        a: 'You may be looking at the live production site (bizcore.sgkapps.com) while a new feature was just deployed to the preview environment. Ask your admin to redeploy to push the latest code to production. New features always land in Preview first.'
      }
    ]
  },
  {
    category: 'Getting Started',
    questions: [
      {
        q: 'How do I create my first invoice?',
        a: 'Go to Sales > Contacts to add a customer first. Then go to Sales > Invoices, click "New Invoice", select the customer, add line items with quantities and prices, and click "Create". You can then download the PDF or send it to the customer.'
      },
      {
        q: 'How do I add employees to the system?',
        a: 'Navigate to HR > Employees. Click "Add Employee" and fill in their personal details, salary structure, and compliance information. The employee will then appear in payroll runs.'
      },
      {
        q: 'How do I process payroll?',
        a: 'Go to Payroll, click "Create Payroll", select the month and year. Toggle "Add Employee Adjustments" to enter overtime hours, deductions, or bonuses for each employee. Click "Create Payroll" to generate the payroll. Then submit for approval, process, and mark as paid.'
      }
    ]
  },
  {
    category: 'Accounts & Finance',
    questions: [
      {
        q: 'What is double-entry accounting?',
        a: 'Double-entry accounting means every transaction affects at least two accounts - a debit and a credit that must balance. For example, when you receive a payment, you debit Cash (increase) and credit Accounts Receivable (decrease).'
      },
      {
        q: 'How do I reconcile a bank account?',
        a: 'Go to Accounts, find your bank account, and click on it to see the drill-down view. Click "Reconcile" to mark transactions that appear on your bank statement. This helps identify any discrepancies between your records and the bank.'
      },
      {
        q: 'How are invoice payments recorded?',
        a: 'When you record a payment against an invoice in Sales > Invoices, the system automatically creates a journal entry debiting Cash/Bank and crediting Accounts Receivable.'
      }
    ]
  },
  {
    category: 'Payroll & HR',
    questions: [
      {
        q: 'How is overtime calculated?',
        a: 'Overtime is calculated based on the employee\'s hourly rate (Basic Salary / 30 days / 8 hours). Multipliers apply: Regular OT = 1.25x, Weekend OT = 1.5x, Holiday OT = 2x. You can also set a custom OT rate per employee.'
      },
      {
        q: 'What is End of Service (EOS) benefits?',
        a: 'EOS is the gratuity paid to employees when they leave the company. The calculation is based on years of service: typically 21 days of salary per year for the first 5 years, and 30 days per year thereafter. You can configure the formula in Payroll > EOS Config.'
      },
      {
        q: 'How do I track leave balances?',
        a: 'Each employee has annual leave days and current balance set in their profile. When processing payroll, you can enter leave days taken, and the balance is automatically adjusted.'
      }
    ]
  },
  {
    category: 'Inventory & Purchases',
    questions: [
      {
        q: 'What is a GRN (Goods Receipt Note)?',
        a: 'A GRN is created when you receive goods from a supplier against a Purchase Order. It records what was actually received, which may differ from what was ordered. GRNs update your inventory levels automatically.'
      },
      {
        q: 'How do stock valuations work?',
        a: 'The system supports FIFO (First In, First Out) and Weighted Average Cost methods. FIFO assumes oldest stock is sold first, while Weighted Average calculates an average cost across all units.'
      },
      {
        q: 'What is an RFQ?',
        a: 'Request for Quotation (RFQ) is sent to suppliers to get price quotes before creating a Purchase Order. Suppliers provide their pricing, and you can then convert the RFQ to a PO with the agreed prices.'
      }
    ]
  },
  {
    category: 'System & Administration',
    questions: [
      {
        q: 'How do I add new users?',
        a: 'Go to Settings > Users. Click "Add User" and enter their email, name, and assign a role. The user will receive an invitation to set up their password.'
      },
      {
        q: 'What are the different user roles?',
        a: 'Super Admin: Full access to everything. Admin: Can manage most settings and approve requests. Manager: Can view reports and approve team requests. Accountant: Focus on financial modules. User: Basic access to assigned modules.'
      },
      {
        q: 'How do backups work?',
        a: 'The system creates automatic daily backups at 2 AM UTC with 30-day retention. You can also create manual backups anytime from Audit & Backup. Configure cloud storage (AWS S3 or Google Cloud) for off-site backup storage.'
      },
      {
        q: 'Can I switch between multiple companies?',
        a: 'Yes! If you have access to multiple companies, use the company switcher in the top navigation to switch between them. Each company has its own data, accounts, and settings.'
      }
    ]
  },
  {
    category: 'Quotations & BOQ Upload',
    questions: [
      {
        q: 'How do I upload a customer\'s BOQ into a quotation?',
        a: 'Open Sales → Quotations → Create. Click the "Upload BOQ" button next to "Add Line", drag in your file (Excel, CSV, Word, PDF, scanned PDF, or image — up to 25 MB), edit the AI-extracted rows in the preview, pick Append / Replace / Smart Merge, and click "Add N items to quotation". The rows drop into the line-items table where you can keep editing them.'
      },
      {
        q: 'What is the difference between Append, Replace, and Smart Merge in the BOQ upload?',
        a: 'Append adds the imported items below your existing rows (safest). Replace clears the current line items and uses only the imported ones. Smart Merge combines duplicate rows with the same description — quantities are summed and the higher unit price wins.'
      },
      {
        q: 'BOQ extraction returned 0 items — what now?',
        a: 'Try a clearer file. For Excel/CSV, make sure the column headers contain words the heuristic recognises (Description / Qty / Quantity / Unit / Rate / Price / Tax). For PDF/image, ensure the text is legible. You can always fall back to manual line entry — the BOQ tool is optional.'
      },
      {
        q: 'How do I add a custom unit like sq.ft or kVA to a line item?',
        a: 'In the Unit dropdown, scroll to the bottom and pick "✏️ Custom…". The cell becomes a free-text input — type your unit. Click the small ✕ button next to it to revert to the preset list.'
      },
      {
        q: 'Where did the per-line discount column go?',
        a: 'Per-line discount has been replaced by a single "Overall Discount" at the quotation level (header section, % or fixed amount). Cleaner PDFs, faster entry, less double-counting risk.'
      },
      {
        q: 'My Project Reference doesn\'t appear on the PDF after editing — why?',
        a: 'This was a bug in earlier versions of the edit endpoint that silently dropped the field. It is fixed now — make sure your tenant is on the latest deployment, then re-edit the quote and re-download the PDF.'
      },
      {
        q: 'Why does the system pop up a confirmation when I try to make an invoice from a Draft quote?',
        a: 'Best practice is to invoice only against quotes the customer has formally accepted. The system intercepts non-approved quotes and offers quick buttons: "Mark Client Approved & continue" or "Mark Agreement Signed & continue", or "Continue anyway" if you want to override.'
      },
      {
        q: 'How do I write custom payment terms like "30% advance, balance on delivery"?',
        a: 'In the Payment Terms dropdown, scroll to the bottom and pick "✏️ Type custom…". The field becomes a free-text input — type whatever you want. The PDF will print it verbatim.'
      },
      {
        q: 'Why is preview/download fast on the second click?',
        a: 'PDFs are cached in memory keyed by the document\'s last edit timestamp. Hitting Preview then Download — or 5 colleagues opening the same invoice — all share the same render. Editing the document automatically busts the cache.'
      }
    ]
  },
  {
    category: 'Quick Notes & Productivity Tools',
    questions: [
      {
        q: 'Where is the Quick Notes notepad?',
        a: 'Look at the topbar — the 📝 icon right next to the 🧮 Calculator opens a personal scratchpad you can use from any page in the app.'
      },
      {
        q: 'Are my Quick Notes synced across devices?',
        a: 'Yes. Notes auto-save to your account (server-synced) about 700ms after each keystroke. Sign in on a different device and the notes are already there.'
      },
      {
        q: 'What happens to my notes if my internet drops?',
        a: 'You can keep typing — the text is cached locally and the footer shows "offline · saved locally". Once you\'re back online, the next save flushes everything to the server. Nothing is lost.'
      },
      {
        q: 'Can my colleagues see my Quick Notes?',
        a: 'No. Quick Notes are private per user per tenant. Different users on the same company have completely separate notepads, and switching to another company you\'re a member of gives you yet another notepad.'
      },
      {
        q: 'What\'s the difference between Quick Notes (topbar) and Sticky Notes (sidebar)?',
        a: 'Quick Notes is a single personal scratchpad always one click away from the topbar — designed for transient TODOs, phone numbers, scratch math. Sticky Notes is a richer board in the sidebar where you can keep multiple coloured/pinned notes long-term.'
      }
    ]
  },
  {
    category: 'Statement of Account (Receivables & Payables)',
    questions: [
      {
        q: 'Where can I see how much each client owes me?',
        a: 'Go to Sidebar → Finance & Accounts → "Statement of Accounts". Pick the customer and a date range — you\'ll see Opening Balance, every invoice & receipt with running balance, and Aging buckets (Current / 1-30 / 31-60 / 61-90 / 90+). Alternative: open the contact card and click "Open Statement of Account" for an instant filtered view.'
      },
      {
        q: 'How do I download a customer\'s ledger as a PDF or Excel?',
        a: 'On the Statement of Accounts page, after picking a customer + date range, click "PDF" (branded landscape print-ready), "Excel" (styled .xlsx) or "CSV" in the top-right. Use "Preview" to open the PDF in a new tab first.'
      },
      {
        q: 'What does the Opening Balance mean?',
        a: 'It is the net of all transactions for this party that happened BEFORE your "From" date. So the statement always reconciles: Opening + Period Activity = Closing. Leave "From" blank to start from the very beginning.'
      },
      {
        q: 'What\'s in the Supplier (Payables) view?',
        a: 'Same screen, just switch to the "Payables (Suppliers)" tab. It combines Purchase Invoices (Bills) and Payment Vouchers to show Total Billed, Total Paid, Balance Payable, and aging across all 5 buckets.'
      },
      {
        q: 'How is the aging calculated?',
        a: 'Days Overdue = today − due date (or invoice date if no due date is set). Buckets: Current (not yet due), 1-30 (overdue by 1-30 days), 31-60, 61-90, 90+. Aging shows the lifetime outstanding balance — even if the invoice falls outside your date range filter.'
      },
      {
        q: 'I want to see ALL customers that owe me money — not one at a time.',
        a: 'On the Statement of Accounts page, do not pick a customer. The page automatically shows an "All Customers" summary table sorted by balance descending, with the aging matrix per row. Click "Open" on any row to drill in.'
      },
      {
        q: 'Can I email this statement directly to my customer?',
        a: 'Not yet (one-click email is on the roadmap once SendGrid is wired up). For now, click "Download → PDF", attach it to your email manually, and send. The PDF already includes your company logo and TRN.'
      }
    ]
  },
  {
    category: 'Progress Invoicing & Deductions',
    questions: [
      {
        q: 'When should I use Progress Invoicing?',
        a: 'Use it whenever you bill a contract in stages — typical for construction, long-running projects, or milestone-based work. Instead of one final invoice, you raise multiple progress bills (e.g. 30%, 60%, then a final 10%).'
      },
      {
        q: 'How do I set a progress invoice for 30% of the contract value?',
        a: 'On Create Invoice, set Billing Type = "Progress / Part-billing Invoice". Pick Progress Mode = "Overall %". Enter "30" in the Overall Progress % field. The system automatically scales the totals — your AED 100,000 contract becomes an AED 30,000 invoice.'
      },
      {
        q: 'What\'s the difference between "Overall %" and "Per-line %"?',
        a: 'Overall % applies one percentage to the whole invoice — fast and simple. Per-line % lets you set a different completion percentage on each line item — useful for multi-stage BOQs where some line items are 100% done while others are still 50%.'
      },
      {
        q: 'What are Retention, Advance, and Previous Payments?',
        a: 'Retention is money the client holds back from this bill (typically 5-10%, released at project close). Advance Received is a prior advance you need to adjust against this invoice. Previous Payments is the sum of previous progress bills already paid. The PDF shows "Final Payable = Total − Retention − Advance − Previous" so the client knows exactly what to pay this time.'
      },
      {
        q: 'Why does the system let me save an invoice with quantity = 0 or price = 0?',
        a: 'Only for Progress invoices — sometimes you raise a final retention-release invoice where the body line items are zero and only the retention reversal carries value. For Standard (non-progress) invoices, qty/price = 0 is still blocked.'
      },
      {
        q: 'I want to clone last month\'s invoice for this month — how?',
        a: 'Open the kebab menu (⋮) on the invoice row → click "Duplicate Invoice". A fresh Draft is created with a new INV-#### number and today\'s date, but the customer, lines, project and all details are preserved. Edit, then Submit.'
      }
    ]
  },
  {
    category: 'Customer / Supplier TRN on Invoices',
    questions: [
      {
        q: 'Where do I enter MY company\'s TRN so it prints on the invoice?',
        a: 'Sidebar → Settings → "Tax & Online Identity" section → fill the "TRN / Tax Registration No." field (accepts UAE TRN like 100123456700003 or Indian GSTIN like 22ABCDE1234F1Z5). Click Save — it now appears in the header of every Invoice / Quotation PDF.'
      },
      {
        q: 'Where do I enter the CUSTOMER\'s TRN?',
        a: 'Open Contacts → click the customer → Edit → fill "Tax ID / VAT TRN" → Save. It now prints under the Bill-To block on every Invoice / Quotation / Credit Note PDF for this customer automatically. Same field works for supplier TRN.'
      },
      {
        q: 'I added the TRN but the old PDF still doesn\'t show it.',
        a: 'PDFs are cached, but the cache is invalidated automatically when a contact or invoice is edited. Click "Preview PDF" again and you should see the new TRN. If you saved the TRN but never reopened the invoice, the cached copy may be stale — open the invoice, save it without changes, and Preview again.'
      },
      {
        q: 'How do I assign Projects to a customer so they auto-fill on invoices?',
        a: 'Open the contact → Edit → "Projects" tab → Add project name, location, reference → Save. Next time you create an invoice for this customer, a "Customer Project" dropdown appears that lets you pick one of these projects and auto-fills Project Reference + Location.'
      }
    ]
  },
  {
    category: 'Reports — Type-aware Forms',
    questions: [
      {
        q: 'Why does the report form change when I pick a different type?',
        a: 'Each report type now has its own purpose-built layout. Pick "Inspection Report" → you get Inspection Type, Inspected By, Findings, Overall Status. Pick "Daily Report" → you get Weather, Manpower, Today\'s Work, Tomorrow\'s Plan. The same applies to Weekly, Monthly, RFI, Incident, and Job Completion. Universal fields (Subject, Date, Priority, Attachments) stay regardless.'
      },
      {
        q: 'Where do I capture the period (from / to) for Weekly and Monthly reports?',
        a: 'When you pick "Weekly Report" or "Monthly Report", the form reveals "Period From", "Period To", and a "Week Label / Month Label" field. The PDF prints these in the meta grid.'
      },
      {
        q: 'I want to track an action that came out of an Inspection — where?',
        a: 'Scroll to the red "Action Required (Optional)" block at the bottom of any non-Completion report. Fill the action description, "Action By" (person or team), and "Target Closure Date". This block renders prominently on the PDF in a red callout so the assignee never misses it.'
      },
      {
        q: 'Can I add my own report type that isn\'t in the dropdown?',
        a: 'Yes — click the "+" button next to the Report Type dropdown, type a name (e.g. "Welding Joint Test Report"), and it\'s added permanently for your company. The form will fall back to the universal layout (Subject + Details + Action block) unless the name contains an existing keyword (e.g. "Inspection" → Inspection layout).'
      },
      {
        q: 'On an Incident Report, what does the Severity badge mean?',
        a: 'Severity drives a coloured badge on the PDF — Minor (green), Moderate (amber), Major (red), Critical (dark red). It\'s separate from Priority and is intended to convey impact of the incident itself, not how fast to handle it.'
      },
      {
        q: 'My RFI hasn\'t been answered yet — should I still save it?',
        a: 'Yes. Fill the Query Subject + Detailed Question and save as Draft. When the response comes in, edit the same report and fill the "Response" section (Response text, Response Date, Responded By). The PDF will then show both the question and the answer.'
      }
    ]
  },
  {
    category: 'Reports — Attachments & Page Footers',
    questions: [
      {
        q: 'Can I attach photos/PDFs to any report type now?',
        a: 'Yes — every report type now supports attachments. Open the report card → Attach → drop JPG / PNG / GIF / WebP / PDF. Images embed inline as a dedicated page (one image per page with a caption). PDFs expand into multiple pages — page 1 of the PDF becomes page X of the report, page 2 becomes page X+1, and so on.'
      },
      {
        q: 'What\'s in the footer on every page?',
        a: 'Every page (including attachment pages) now shows: company name on the left, "Page X of Y" centered, report number on the right. So if you generate a report with 3 attachment pages, you\'ll see Page 1 of 4 → Page 4 of 4 all with the same footer.'
      },
      {
        q: 'My attachments aren\'t showing in the PDF — what changed?',
        a: 'After attaching or removing files, the PDF cache is invalidated automatically — the next time you click PDF/Preview, the document is re-rendered fresh. If you still don\'t see them, refresh the browser page (Ctrl/Cmd+R) and try again.'
      }
    ]
  },
  {
    category: 'Document Summary Bar (NEW)',
    questions: [
      {
        q: 'What is the new KPI bar I see above my Invoices / Quotations / POs?',
        a: 'It\'s the Document Summary Bar. It appears above the list on Sales (Invoices + Quotations), Purchases (PO + RFQ + GRN), Vouchers (Payment + Receipt) and Inventory tabs. At a glance you get: Count, Total Value, Outstanding, This Month, Approved, Pending. The numbers always reflect your current search/filter on the page.'
      },
      {
        q: 'What does the "Summary" button do?',
        a: 'It opens a side drawer with a deeper drill-down: status breakdown with amounts, payment-method split (for vouchers), and your Top 10 Counterparties by value. Inventory also gets Stock Value, Low Stock and Out of Stock cards. Inside the drawer you can download a branded PDF or styled Excel of the summary.'
      },
      {
        q: 'How do I download the summary as a PDF or Excel?',
        a: 'Use the small icons on the right of the KPI bar: 👁 eye = preview PDF in a new tab, 📄 PDF = download branded PDF, 📊 Excel = download styled .xlsx with a Summary sheet plus a Documents sheet listing every row in the current filter. Same buttons are also available inside the Summary drawer.'
      },
      {
        q: 'Does the summary respect my filter / search?',
        a: 'Yes — type in the search box and the KPI bar (and the PDF/Excel you download) only counts the matching rows. Same applies to status filters where available.'
      },
      {
        q: 'I don\'t see any numbers on the bar — why?',
        a: 'Either the list is empty for the current filter, or the summary is still loading. The bar shows "Loading {label} summary…" while fetching. If it stays empty, click the 🔄 Refresh icon on the right of the bar.'
      }
    ]
  },
  {
    category: 'Invoice Types & Retention',
    questions: [
      {
        q: 'What\'s the difference between Tax Invoice, Proforma Invoice and Invoice?',
        a: 'Tax Invoice = standard VAT-registered tax document — full QR code on PDF, used after agreement is signed. Proforma Invoice = preliminary quote-like document — amber "not a tax invoice" badge on PDF, NO QR, no accounting impact (used to give a customer a value to release advance / LPO). Invoice = plain non-VAT case — removes the word "TAX" from the title. Switch between them via the "Invoice Type" dropdown on the Create/Edit Invoice form.'
      },
      {
        q: 'When should I send a Proforma Invoice?',
        a: 'Before the customer has signed the agreement / issued an LPO. It documents the agreed value so they can release an advance, but doesn\'t commit you to a tax filing yet. Once approved, change the type to Tax Invoice (or duplicate it as one) — the QR will appear and the document becomes part of your VAT return.'
      },
      {
        q: 'How does Retention math work now?',
        a: 'Retention is now deducted BEFORE VAT (industry standard for contractors). The PDF shows: Subtotal → less Retention → Net Taxable → +VAT (on net taxable) → Gross → less Advance → less Previous Payments → Current Payable. Example: 32,500 - 3,250 retention = 29,250 net taxable + 5% VAT = 30,712.50 gross.'
      },
      {
        q: 'My old invoices show the OLD retention math — should I worry?',
        a: 'No — only newly created/edited invoices use the new order. Old invoices remain frozen as you originally generated them. To re-issue, click Duplicate on the invoice and it will pick up the new math.'
      }
    ]
  },
  {
    category: 'Job Completion Reports',
    questions: [
      {
        q: 'Where do I create a Job Completion Report?',
        a: 'Reports module → New Report → set Report Type = "Job Completion Report". An amber panel will unlock with the full handover layout: Project Reference, Project Location, Client Name, LPO/Agreement Reference, Work Details, editable Confirmation Statement (default text auto-fills), Additional Notes, Client Signatory Name + Sign Date, and a digital signature pad (mouse + touch — exports a PNG of the signature).'
      },
      {
        q: 'How do I sign on the screen?',
        a: 'Use the canvas at the bottom of the Job Completion panel. Click and drag with the mouse (or use your finger on a tablet). The signature is saved as a PNG and embedded in the PDF along with the signatory\'s name and date.'
      },
      {
        q: 'What gets embedded in the PDF?',
        a: 'Image attachments (jpg/png) embed inline as full pages. Non-image files (pdf/doc) appear in an "Attached Files" appendix list. The signature, signatory name and date appear in a dedicated block above the footer. "Page X of Y" includes attachment pages.'
      }
    ]
  }
];

const TIPS = [
  {
    icon: Sparkle,
    title: 'AI Job Orders (NEW)',
    tip: 'Operations → Job Orders → New Job Order → pick equipment → click "⚡ AI add for equipment" to auto-generate the entire checklist. Hit "✨ AI draft" on the MoS and RA panels for instant Method of Statement + Risk Assessment.'
  },
  {
    icon: Warehouse,
    title: 'Multi-Warehouse Inventory (NEW)',
    tip: 'Inventory → Warehouses → Add Warehouse. Then use Transfers tab to move stock between Main Store ↔ Site / Branch / Van. Each item now has a Bin Card showing every movement per warehouse.'
  },
  {
    icon: FileText,
    title: 'Quotation Diff Viewer (NEW)',
    tip: 'On Quotations, toggle "Show Revisions", tick any two revisions of the same quote, click "Compare" — added/removed/changed lines and totals are highlighted side-by-side.'
  },
  {
    icon: Sparkle,
    title: 'Document Summary Bar (NEW)',
    tip: 'Every Sales / Purchases / Vouchers / Inventory tab now has a KPI bar above the list — Count, Total Value, Outstanding, This Month, Approved, Pending. Click 👁 to preview a branded summary PDF, or 📊 to download Excel.'
  },
  {
    icon: Sparkle,
    title: 'AI BOQ Upload',
    tip: 'In the Quotation create form, click "Upload BOQ" to drop in an Excel/CSV/Word/PDF/scanned image. AI extracts the line items, you preview & edit, then Append/Replace/Smart-Merge into the quote.'
  },
  {
    icon: NotePencil,
    title: 'Quick Notes (synced)',
    tip: 'Click the 📝 icon in the topbar (next to the calculator) for a personal scratchpad that auto-saves to your account every keystroke. Works on every device you sign in to, even offline.'
  },
  {
    icon: Calculator,
    title: 'Topbar Calculator',
    tip: 'Need a quick number? The 🧮 calculator in the topbar opens on any page — no app switching while you\'re writing a quote.'
  },
  {
    icon: Lightbulb,
    title: 'Keyboard Shortcuts',
    tip: 'Use Ctrl+K (or Cmd+K on Mac) to open the quick search. Press / to focus the search bar on any page.'
  },
  {
    icon: Receipt,
    title: 'Quick Quotation to Invoice',
    tip: 'Click the "Convert to Invoice" button on any approved quotation to instantly create an invoice with all the same details. The system will warn you if the quote isn\'t in an approved status yet.'
  },
  {
    icon: Receipt,
    title: 'Statement of Account in 3 clicks',
    tip: 'Finance & Accounts → Statement of Accounts → pick customer → done. See every invoice, every receipt, aging buckets, and download as PDF/Excel/CSV to share with the client.'
  },
  {
    icon: Receipt,
    title: 'Progress Invoicing for Construction',
    tip: 'Create Invoice → Billing Type = "Progress" → Overall % = 30 → invoice auto-scales to 30% of the contract. Add Retention / Advance / Previous Payments and the Final Payable is computed and printed on the PDF.'
  },
  {
    icon: Receipt,
    title: 'Duplicate Invoice in 1 click',
    tip: 'Recurring billing? Open the kebab (⋮) on any invoice row → "Duplicate Invoice" — instantly clones it as a fresh draft with a new INV-#### number.'
  },
  {
    icon: Calendar,
    title: 'Never Miss Renewals',
    tip: 'The Calendar automatically shows upcoming expiry dates for employee visas, passports, insurance, and company compliance documents.'
  },
  {
    icon: Kanban,
    title: 'Drag & Drop Tasks',
    tip: 'Simply drag task cards between columns to update their status. No need to open and edit each task individually.'
  },
  {
    icon: CloudArrowUp,
    title: 'Automatic Backups',
    tip: 'Enable auto-backup in Settings to ensure your data is backed up daily. Configure cloud storage for additional security.'
  }
];

export default function HelpPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('guide');

  const filteredModules = MODULES.filter(m =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredFAQs = FAQS.map(category => ({
    ...category,
    questions: category.questions.filter(q =>
      q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Book className="w-7 h-7 text-primary" />
            Help & User Guide
          </h1>
          <p className="text-muted-foreground mt-1">Learn how to use ERP Hub effectively</p>
          <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#0f172a] text-[#d4a84b] text-[11px] font-mono font-semibold dark:bg-[#d4a84b] dark:text-[#0f172a]" data-testid="help-revision-badge">
            Revision {APP_REVISION.revisionCode} · Released {APP_REVISION.releasedOn}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-xl">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          placeholder="Search help topics, FAQs, modules..."
          className="pl-10 h-12"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* QR card — link to public User Guide PDF (Feb 2026) */}
      <HelpQRCard />

      {/* Quick Tips */}
      {!searchQuery && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {TIPS.map((tip, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-card border border-border rounded-xl p-4"
            >
              <div className="p-2 rounded-lg bg-primary/20 w-fit mb-3">
                <tip.icon className="w-5 h-5 text-primary" />
              </div>
              <h4 className="font-semibold text-sm mb-1">{tip.title}</h4>
              <p className="text-xs text-muted-foreground">{tip.tip}</p>
            </motion.div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="guide" className="flex items-center gap-2">
            <Book className="w-4 h-4" />
            Module Guide
          </TabsTrigger>
          <TabsTrigger value="faq" className="flex items-center gap-2">
            <Question className="w-4 h-4" />
            FAQ
          </TabsTrigger>
        </TabsList>

        {/* Module Guide Tab */}
        <TabsContent value="guide" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredModules.map((module, idx) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-card border border-border rounded-xl p-5"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-primary/20 flex-shrink-0">
                    <module.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{module.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{module.description}</p>
                    <ul className="space-y-1">
                      {module.features.map((feature, fIdx) => (
                        <li key={fIdx} className="text-sm flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {filteredModules.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Book className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No modules found matching "{searchQuery}"</p>
            </div>
          )}
        </TabsContent>

        {/* FAQ Tab */}
        <TabsContent value="faq" className="mt-6">
          <div className="space-y-6 max-w-3xl">
            {filteredFAQs.map((category, idx) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-card border border-border rounded-xl overflow-hidden"
              >
                <div className="bg-muted/50 px-5 py-3 border-b border-border">
                  <h3 className="font-semibold">{category.category}</h3>
                </div>
                
                <Accordion type="single" collapsible className="px-2">
                  {category.questions.map((q, qIdx) => (
                    <AccordionItem key={qIdx} value={`${category.category}-${qIdx}`} className="border-b-0">
                      <AccordionTrigger className="px-3 py-4 text-left hover:no-underline">
                        <span className="flex items-center gap-2">
                          <Question className="w-4 h-4 text-primary flex-shrink-0" />
                          {q.q}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-4 pt-0">
                        <div className="pl-6 text-muted-foreground">
                          {q.a}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            ))}
            
            {filteredFAQs.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Question className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No FAQs found matching "{searchQuery}"</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Contact Support */}
      <div className="bg-card border border-border rounded-xl p-6 mt-8">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-green-500/20">
            <ChatCircle className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Need More Help?</h3>
            <p className="text-muted-foreground mb-4">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => window.location.href = '/dashboard/chat'}>
                <ChatCircle className="w-4 h-4 mr-2" />
                Contact Team
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
